import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
// motion moved into LearningGenerationScreen and layout components
import { useAuthStore, useLearningStore, useTutorStore, useAdaptiveStore } from '@shared/state';
import {
  getPathForContent,
  triggerPathGeneration,
  loadNodeContent,
  stopLearningPath,
  deleteLearningPath,
} from '@shared/services/learningPath';
import { onPathAdjusted, triggerContentRegeneration } from '@shared/services/adaptive';
import { cacheLearningSession, getCachedLearningSession } from '@shared/services/learning-cache';
import { loadPreviousSessions } from '@shared/services/tutor';
import { trackEvent, getBaseProperties, flushEvents } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import type { NodeContent } from '@shared/types/learning-path';
import type { SubscriptionTier } from '@shared/constants/tiers';
// RidgeTexture moved into LearningGenerationScreen
import { ConceptMapLayout } from '../components/learning/ConceptMapLayout';
import { ExportMenu } from '../components/learning/ExportMenu';
import { LinearLayout } from '../components/learning/LinearLayout';
import { CardGridLayout } from '../components/learning/CardGridLayout';
import { LearningGenerationScreen } from '../components/learning/LearningGenerationScreen';
import { TutorPanel, TutorFAB } from '../components/tutor/TutorPanel';
import { TextHighlightTrigger } from '../components/tutor/TextHighlightTrigger';
import { AdjustmentBanner } from '../components/adaptive/AdjustmentBanner';

export function LearningPage() {
  const { contentId } = useParams<{ contentId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const store = useLearningStore();
  const tutorStore = useTutorStore();
  const adaptiveStore = useAdaptiveStore();
  const [regeneratingNodeId, setRegeneratingNodeId] = useState<string | null>(null);

  // Auto-open tutor panel when navigated here with ?tutor=open
  useEffect(() => {
    if (searchParams.get('tutor') !== 'open') return;
    if (!store.currentPath) return; // wait for path to load before opening
    tutorStore.openPanel();
    const next = new URLSearchParams(searchParams);
    next.delete('tutor');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentPath, searchParams]);

  const [nodeContents, setNodeContents] = useState<Map<string, NodeContent>>(new Map());
  const [initializing, setInitializing] = useState(true);
  const [nodeEntryTime, setNodeEntryTime] = useState<number>(Date.now());
  // Content generation runs server-side — client just listens
  const [sessionStartTime] = useState<number>(Date.now());
  const contentRef = useRef<HTMLDivElement>(null);

  // Track lg breakpoint for Tutor-panel padding (panel overlays at < lg).
  const [isLargeViewport, setIsLargeViewport] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = () => setIsLargeViewport(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Fast path: hydrate from the single-slot IndexedDB cache so a reload of
  // the most recently studied content renders instantly. The background
  // Firestore fetch below still runs to refresh progress + any new nodes.
  useEffect(() => {
    if (!user || !contentId) return;
    let cancelled = false;
    (async () => {
      const cached = await getCachedLearningSession(contentId, user.uid);
      if (cancelled || !cached) return;
      // Don't clobber a fresh Firestore load that may have already landed.
      if (useLearningStore.getState().currentPath) return;
      store.setPath(cached.path);
      setNodeContents(new Map(Object.entries(cached.nodeContents)));
      setInitializing(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, contentId]);

  // Persist the session to IndexedDB once it's fully loaded, and any time
  // the path or node contents change (progress updates, node regeneration).
  useEffect(() => {
    if (!user || !contentId) return;
    if (!store.currentPath || store.currentPath.generationStatus !== 'complete') return;
    if (nodeContents.size === 0) return;
    void cacheLearningSession({
      contentId,
      userId: user.uid,
      path: store.currentPath,
      nodeContents,
    });
  }, [user, contentId, store.currentPath, nodeContents]);

  // Initialize: find or create path
  useEffect(() => {
    if (!user || !contentId) return;

    (async () => {
      const existing = await getPathForContent(contentId, user.uid);

      if (existing) {
        if (existing.generationStatus === 'complete') {
          store.loadPath(existing.pathId);
        } else {
          const unsub = store.listenToGeneration(existing.pathId);
          store.loadPath(existing.pathId);

          // Content generation runs server-side — just listen for updates

          setInitializing(false);
          return () => unsub();
        }
      } else {
        try {
          const pathId = await triggerPathGeneration(contentId);
          const unsub = store.listenToGeneration(pathId);

          // Content generation runs server-side automatically after LA-001
          setInitializing(false);
          return () => { unsub(); };
        } catch (err) {
          console.error('Path generation error:', err);
        }
      }

      setInitializing(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, contentId]);

  // Load node contents + track session start when path is ready
  useEffect(() => {
    if (!store.currentPath || store.currentPath.generationStatus !== 'complete' || !contentId) return;

    (async () => {
      const contents = new Map<string, NodeContent>();
      for (const node of store.currentPath!.nodes) {
        if (node.contentGenerated && node.contentRef) {
          const content = await loadNodeContent(store.currentPath!.pathId, node.nodeId);
          if (content) contents.set(node.nodeId, content);
        }
      }
      setNodeContents(contents);

      const startNode = store.currentPath!.progress.currentNodeId ?? store.currentPath!.nodes[0]?.nodeId;
      if (startNode) store.navigateToNode(startNode);

      // Track learning session started
      trackEvent({
        name: ANALYTICS_EVENTS.LEARNING_SESSION_STARTED,
        properties: {
          ...getBaseProperties(),
          pathId: store.currentPath!.pathId,
          contentId,
          layoutType: store.currentPath!.layout,
          totalNodes: store.currentPath!.nodes.length,
          completedNodes: store.currentPath!.progress.completedNodes.length,
          isResume: store.currentPath!.progress.completedNodes.length > 0,
        },
      }).catch(() => {});

      // Set Tutor context
      tutorStore.setContext(contentId, store.currentPath!.pathId, startNode ?? null);
      tutorStore.loadQueryLimits();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentPath?.generationStatus]);

  // Tutor session management: create or resume the most recent unended session
  // for this contentId. Staleness is not capped — a self-directed learner
  // returning to a document days later should continue the conversation, not
  // start fresh. (Server-side context trimming handles long histories.)
  useEffect(() => {
    if (!store.currentPath || !contentId || !user) return;
    if (tutorStore.currentSession) return; // already have one

    (async () => {
      const prevSessions = await loadPreviousSessions(user.uid, contentId, 1);
      const recent = prevSessions[0];
      if (recent && recent.endedAt === null) {
        tutorStore.loadSession(recent.sessionId);
        return;
      }
      // Don't create until user opens the panel — avoid unnecessary writes
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentPath, contentId, user]);

  // Listen for adaptive engine adjustments
  useEffect(() => {
    if (!store.currentPath) return;
    const pathId = store.currentPath.pathId;

    // Load any pending adjustment
    adaptiveStore.loadAdjustmentState(pathId);

    // Listen for new adjustments
    const unsubPath = onPathAdjusted(pathId, (message) => {
      if (message) {
        adaptiveStore.setAdjustmentMessage(message);
      }
    });

    return () => unsubPath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentPath?.pathId]);

  // Track session end on unmount
  useEffect(() => {
    return () => {
      if (!store.currentPath || !contentId) return;
      const totalTimeMs = Date.now() - sessionStartTime;
      const completedNodes = store.currentPath.progress.completedNodes.length;

      trackEvent({
        name: totalTimeMs < 60000 && completedNodes === 0
          ? ANALYTICS_EVENTS.LEARNING_SESSION_ABANDONED
          : ANALYTICS_EVENTS.LEARNING_SESSION_ENDED,
        properties: {
          ...getBaseProperties(),
          ...(totalTimeMs < 60000 && completedNodes === 0
            ? {
                pathId: store.currentPath.pathId,
                lastNodeId: store.currentNodeId,
                timeInSessionMs: totalTimeMs,
              }
            : {
                pathId: store.currentPath.pathId,
                totalTimeMs,
                nodesCompleted: completedNodes,
                nodesSkipped: 0,
                checkpointsCompleted: Object.keys(store.currentPath.progress.checkpointScores).length,
              }),
        },
      } as Parameters<typeof trackEvent>[0]).catch(() => {});

      flushEvents().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigate = useCallback((nodeId: string) => {
    if (!store.currentPath || !contentId) return;

    if (store.currentNodeId) {
      const timeMs = Date.now() - nodeEntryTime;
      if (timeMs > 2000) {
        const prevNode = store.currentPath.nodes.find((n) => n.nodeId === store.currentNodeId);
        trackEvent({
          name: ANALYTICS_EVENTS.LEARNING_SECTION_COMPLETED,
          properties: {
            ...getBaseProperties(),
            pathId: store.currentPath.pathId,
            nodeId: store.currentNodeId,
            timeOnSectionMs: timeMs,
            expectedDurationMs: (prevNode?.estimatedDuration ?? 5) * 60000,
          },
        }).catch(() => {});
      }

      if (store.currentPath.progress.completedNodes.includes(nodeId)) {
        trackEvent({
          name: ANALYTICS_EVENTS.LEARNING_SECTION_REVISITED,
          properties: {
            ...getBaseProperties(),
            pathId: store.currentPath.pathId,
            nodeId,
            timeSinceFirstViewMs: Date.now() - nodeEntryTime,
          },
        }).catch(() => {});
      }
    }

    setNodeEntryTime(Date.now());
    store.navigateToNode(nodeId);

    // Update Tutor context
    tutorStore.setContext(contentId, store.currentPath.pathId, nodeId);

    const targetNode = store.currentPath.nodes.find((n) => n.nodeId === nodeId);

    // Lazy content regeneration: if AE-003 modified this node, regenerate content
    if (targetNode && targetNode.contentGenerated === false) {
      setRegeneratingNodeId(nodeId);
      triggerContentRegeneration(store.currentPath.pathId, nodeId)
        .then(async () => {
          // Reload the regenerated content
          const content = await loadNodeContent(store.currentPath!.pathId, nodeId);
          if (content) {
            setNodeContents((prev) => new Map(prev).set(nodeId, content));
          }
        })
        .finally(() => setRegeneratingNodeId(null));
    }

    trackEvent({
      name: ANALYTICS_EVENTS.LEARNING_SECTION_VIEWED,
      properties: {
        ...getBaseProperties(),
        pathId: store.currentPath.pathId,
        nodeId,
        nodeType: targetNode?.nodeType ?? 'learn',
        contentFormat: targetNode?.contentFormat ?? 'textual',
        depthLevel: targetNode?.depthLevel ?? 'standard',
      },
    }).catch(() => {});
  }, [store, tutorStore, contentId, nodeEntryTime]);

  const handleComplete = useCallback((nodeId: string) => {
    store.completeNode(nodeId);
  }, [store]);

  // Auth guard
  if (!user) {
    navigate('/login');
    return null;
  }

  // Generation in progress
  if (initializing || !store.currentPath || store.currentPath.generationStatus !== 'complete') {
    const pathId = store.currentPath?.pathId;
    return <LearningGenerationScreen
      generationStatus={store.generationStatus}
      generationProgress={store.generationProgress}
      onReturnToDashboard={() => navigate('/dashboard')}
      onStop={pathId ? () => {
        stopLearningPath(pathId);
        navigate('/dashboard');
      } : undefined}
      onDelete={pathId ? () => {
        deleteLearningPath(pathId);
        store.reset();
        navigate('/dashboard');
      } : undefined}
    />;
  }

  // Determine layout width based on Tutor panel. The panel is resizable on
  // desktop, so its width is tracked in the tutor store. At < lg the panel
  // overlays content (existing behavior), so no padding is applied.
  const isTutorOpen = tutorStore.isOpen;
  const tutorPanelWidth = tutorStore.panelWidth;
  const layoutStyle = isTutorOpen && isLargeViewport ? { paddingRight: tutorPanelWidth } : undefined;

  const layout = store.currentPath.layout;

  return (
    <div ref={contentRef} className="relative transition-[padding] duration-150" style={layoutStyle}>
      {/* Adaptive engine adjustment banner */}
      {adaptiveStore.showAdjustmentBanner && adaptiveStore.adjustmentMessage && store.currentPath && (
        <div className="px-4 pt-4">
          <AdjustmentBanner
            message={adaptiveStore.adjustmentMessage}
            visible={true}
            onDismiss={() => adaptiveStore.dismissAdjustment(store.currentPath!.pathId)}
          />
        </div>
      )}

      {/* Content regeneration shimmer */}
      {regeneratingNodeId && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg bg-dark-surface border border-dark-border shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-content-secondary font-body">Updating content for you...</span>
          </div>
        </div>
      )}

      {/* Text highlight trigger */}
      <TextHighlightTrigger containerRef={contentRef} />

      {/* Path hero illustration (Recraft) */}
      {store.currentPath?.heroImageUrl && (
        <div className="px-4 pt-4">
          <img
            src={store.currentPath.heroImageUrl}
            alt=""
            className="w-full h-40 object-cover rounded-xl border border-dark-border"
          />
        </div>
      )}

      {/* Export toolbar — available once the path is fully generated */}
      {store.currentPath && store.currentPath.generationStatus === 'complete' && (
        <div className="px-4 pt-4 flex justify-end">
          <ExportMenu
            pathId={store.currentPath.pathId}
            tier={(user?.subscription ?? 'free') as SubscriptionTier}
            onLockedClick={() => navigate('/pricing')}
          />
        </div>
      )}

      {/* Layout */}
      {layout === 'concept_map' && (
        <ConceptMapLayout
          path={store.currentPath}
          nodeContents={nodeContents}
          currentNodeId={store.currentNodeId}
          onNavigate={handleNavigate}
          onNodeContentUpdated={(nodeId, content) => {
            setNodeContents((prev) => {
              const next = new Map(prev);
              next.set(nodeId, content);
              return next;
            });
          }}
        />
      )}
      {layout === 'linear_scroll' && (
        <LinearLayout
          path={store.currentPath}
          nodeContents={nodeContents}
          currentNodeId={store.currentNodeId}
          onNavigate={handleNavigate}
          onComplete={handleComplete}
        />
      )}
      {layout === 'card_grid' && (
        <CardGridLayout
          path={store.currentPath}
          nodeContents={nodeContents}
          currentNodeId={store.currentNodeId}
          onNavigate={handleNavigate}
          onComplete={handleComplete}
        />
      )}

      {/* Tutor FAB + Panel */}
      <TutorFAB />
      <TutorPanel />
    </div>
  );
}
